import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Grid, makeStyles, Typography } from '@material-ui/core';
import { Apartment, Person, Star } from '@material-ui/icons';
import { getClientCard, getKSKDebt } from 'api';
import { getKskDebtTotal, numberWithSpaces } from 'helper';
import { IClientCardModel, IClientContract, IClientObjectModel } from 'interfaces';
import SensitiveValue from 'components/SensitiveValue';
import ClientComments from './ClientComments';
import ClientCardHeader from './ClientCardHeader';
import ClientNpsBlock from './ClientNpsBlock';
import ClientObjectCard, { objectKey, OtherContractsCard } from './ClientObjectCard';
import { sameGuid, Section, SUBSTRATE_COLOR, useCardStyles } from './helpers';

interface IProps {
  phone?: string | null;
  /** Помещение и контрагент 1С — нужны для расчёта задолженности (ДЗ) по выбранному помещению. */
  roomGuid?: string | null;
  ownerGuid1c?: string | null;
}

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: SUBSTRATE_COLOR,
    borderRadius: 12,
    padding: theme.spacing(2),
  },
  debt: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    padding: '14px 18px',
    borderRadius: 8,
    backgroundColor: '#fdecec',
    border: '1px solid #f3c4c4',
    color: '#8a1c1c',
    marginBottom: theme.spacing(2),
  },
  debtTitle: {
    fontSize: 14,
    fontWeight: 500,
    marginRight: theme.spacing(2),
  },
  debtAmount: {
    fontSize: 16,
    fontWeight: 700,
  },
  contactRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    padding: '8px 16px',
  },
  empty: {
    padding: theme.spacing(2),
  },
  noData: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.error.main,
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
}));

// Договоры объекта — по совпадению помещения.
const contractsOfObject = (contracts: IClientContract[], object: IClientObjectModel): IClientContract[] =>
  contracts.filter((contract) => sameGuid(contract.roomGuid, object.roomGuid));

// Клиента нет в реестре all_clients (404): карточка рисуется в том же макете,
// что и найденная, но все поля — «-», списки — пустые.
const EMPTY_CARD: IClientCardModel = {
  fio: null,
  iin: null,
  biClubCardType: null,
  isEmployee: null,
  npsLiving: [],
  contracts: [],
  applicationsCount: null,
  objects: [],
  npsHistory: [],
  spousePhoneNumber: null,
  hasSensitiveAccess: null,
};

/**
 * Полная карточка клиента (светлая тема) для таба «Карточка клиента» в журнале
 * звонка / заявке. Шапка (ФИО, статусы, ИИН), объекты с договорами, комментарии,
 * NPS и доп. контакты. Данные подгружаются по номеру
 * телефона из реестра all_clients; пустые поля и секции показываются как «-».
 * Чувствительные поля (ИИН) приходят с бэка только при доступе второго уровня.
 */
const ClientCard: React.FC<IProps> = ({ phone, roomGuid, ownerGuid1c }) => {
  const classes = useStyles();
  const cardClasses = useCardStyles();
  const [card, setCard] = useState<IClientCardModel | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false);
  const [debtTotal, setDebtTotal] = useState(0);

  useEffect(() => {
    const digits = (phone || '').replace(/\D/g, '');
    if (digits.length < 10) {
      setCard(null);
      setNotFound(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getClientCard(phone as string)
      .then((data) => {
        if (cancelled) return;
        setCard(data || null);
        setNotFound(!data);
      })
      .catch((error) => {
        if (cancelled) return;
        setCard(null);
        // 404 «Нет данных» — клиента нет в реестре all_clients: рисуем ту же карточку, но пустую.
        setNotFound(error?.response?.status === 404);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [phone]);

  // ДЗ считается по выбранному помещению: без него (журнал звонка, поиск) блок не показываем.
  useEffect(() => {
    if (!roomGuid || !ownerGuid1c) {
      setDebtTotal(0);
      return;
    }

    let cancelled = false;
    getKSKDebt(roomGuid, ownerGuid1c)
      .then((debt) => {
        if (!cancelled) setDebtTotal(getKskDebtTotal(debt));
      })
      .catch(() => {
        // расчёт задолженности недоступен (интеграция с 1С) — просто не показываем блок
        if (!cancelled) setDebtTotal(0);
      });

    return () => {
      cancelled = true;
    };
  }, [roomGuid, ownerGuid1c]);

  const debtBlock: React.ReactNode =
    debtTotal > 0 ? (
      <Box className={classes.debt}>
        <Typography className={classes.debtTitle}>ДЗ: задолженность по услугам сервисного обслуживания</Typography>
        <Typography className={classes.debtAmount}>К оплате: {numberWithSpaces(debtTotal.toFixed(2))} тенге</Typography>
      </Box>
    ) : null;

  // заголовок секции со счётчиком комментариев рисует сам ClientComments — он знает их число
  const commentsSection: React.ReactNode = phone ? <ClientComments phone={phone} /> : null;

  if (loading) {
    return (
      <Box className={classes.loader}>
        <CircularProgress />
      </Box>
    );
  }

  // Номер не введён (или запрос упал не по «не найдено») — карточки ещё нет, ДЗ по
  // выбранному помещению считается независимо от неё, поэтому плашку всё равно показываем.
  if (!card && !notFound) {
    return (
      <Box className={classes.root}>
        {debtBlock}
        <Box className={classes.empty}>
          <Typography variant="body2" color="textSecondary">
            Введите номер телефона, чтобы загрузить карточку клиента
          </Typography>
        </Box>
      </Box>
    );
  }

  // Клиента нет в реестре all_clients (404) — та же компоновка, что у найденной
  // карточки, но со значениями «-»; задолженность и комментарии по номеру доступны как обычно.
  const displayedCard = card ?? EMPTY_CARD;
  const objects = displayedCard.objects || [];
  const contracts = displayedCard.contracts || [];
  const otherContracts = contracts.filter(
    (contract) => !objects.some((object) => sameGuid(contract.roomGuid, object.roomGuid)),
  );
  const clientPhone = phone as string;

  return (
    <Box className={classes.root}>
      {/* Задолженность по услугам сервисного обслуживания (ДЗ) по выбранному помещению */}
      {debtBlock}

      <ClientCardHeader card={displayedCard} phone={clientPhone} />

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Section icon={<Apartment className={cardClasses.sectionIcon} />} title="Объекты и договоры">
            {objects.map((object, index) => (
              <ClientObjectCard
                key={objectKey(object, index)}
                object={object}
                contracts={contractsOfObject(contracts, object)}
                phone={clientPhone}
              />
            ))}
            {otherContracts.length > 0 && <OtherContractsCard contracts={otherContracts} />}
            {objects.length === 0 && contracts.length === 0 && (
              <Typography className={classes.noData}>Нет данных</Typography>
            )}
          </Section>
          {/* Оценки NPS живущих (living_mart, свежие сверху) и история оценок */}
          <Section icon={<Star className={cardClasses.sectionIcon} />} title="NPS">
            <ClientNpsBlock npsLiving={displayedCard.npsLiving || []} npsHistory={displayedCard.npsHistory || []} />
          </Section>
        </Grid>

        <Grid item xs={12} md={4}>
          {commentsSection}

          <Section icon={<Person className={cardClasses.sectionIcon} />} title="Доп. контакты">
            <Box className={`${cardClasses.card} ${classes.contactRow}`}>
              <Typography className={cardClasses.fieldLabel}>Телефон супруга(и)</Typography>
              <Box className={cardClasses.fieldValue}>
                <SensitiveValue masked={displayedCard.spousePhoneNumber} phone={clientPhone} field="SPOUSE_PHONE" />
              </Box>
            </Box>
          </Section>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClientCard;
